import React from "react";
import HistoricoAmbiente from "../../components/HistoricoAmbiente";
import { AnimatePresence } from "framer-motion";
import { FiCheck, FiClock, FiX, FiEye, FiEyeOff } from "react-icons/fi";
import { createPortal } from "react-dom";
import { formatarData, formatarMoeda, diasUteis } from "../../utils/calculos";
import { toDate } from "../../utils/status";
import { buildAmbienteEtapas } from "../../utils/ambienteTimeline";
import {
  parseMarceneiro,
  statusTerceirosAmbiente,
  statusAcabamentoAmbiente,
} from "../../utils/entregas";
import {
  Overlay,
  Panel,
  Header,
  HeaderTop,
  Eyebrow,
  CloseButton,
  Titulo,
  Subtitulo,
  StatusPill,
  Body,
  InfoGrid,
  InfoCard,
  InfoTop,
  InfoLabel,
  InfoValor,
  InfoSub,
  EyeButton,
  Avatar,
  RecebChips,
  RecebChip,
  SectionLabel,
  TL,
  TLItem,
  TLDot,
  TLContent,
  TLTitulo,
  TLData,
  TLBadge,
} from "./drawerStyles";

const EMPTY = "—";

function iniciais(nome) {
  const partes = String(nome || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

function relativoEntrega(dataAlvo) {
  const d = toDate(dataAlvo);
  if (!d) return { texto: "sem data", tone: null };
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const alvo = new Date(d);
  alvo.setHours(0, 0, 0, 0);
  if (alvo.getTime() === hoje.getTime()) return { texto: "é hoje", tone: "danger" };
  if (alvo < hoje) {
    const dias = Math.round(diasUteis(alvo, hoje));
    return { texto: `atrasado ${dias} d.u.`, tone: "danger" };
  }
  const dias = Math.round(diasUteis(hoje, alvo));
  return {
    texto: `em ${dias} dia${dias === 1 ? "" : "s"} úteis`,
    tone: dias <= 5 ? "warning" : null,
  };
}

function getTLIcon(status) {
  if (status === "concluida") return <FiCheck size={13} />;
  if (status === "atual") return <FiClock size={13} />;
  return null;
}

// Rótulo de data/estado da etapa — mesma semântica do sidebar das demais
// telas legadas (AmbienteDetalhesDrawer/Timeline).
function getDataLabel(etapa) {
  if (etapa.status === "atual") return "Em andamento";
  if (etapa.status === "futura") return "Aguardando…";
  if (etapa.implicita) return "Concluído (implícito)";
  return formatarData(etapa.data) || "Concluído";
}

function resumoRecebimentos(raw) {
  const terceiros = statusTerceirosAmbiente(raw);
  const acabamento = statusAcabamentoAmbiente(raw);
  const chips = [];
  if (terceiros.aplicavel) {
    chips.push({
      label: `Terceiros ${terceiros.completo ? "ok" : "pendente"}`,
      tone: terceiros.completo ? "success" : "warning",
    });
  }
  if (acabamento.aplicavel) {
    chips.push({
      label: `Acabamento ${acabamento.completo ? "ok" : "pendente"}`,
      tone: acabamento.completo ? "success" : "warning",
    });
  }
  return chips;
}

/**
 * Drawer de consulta de um ambiente na fila de entregas (mockup 2026-07).
 * Grid de dados + linha do tempo de produção + histórico de comentários
 * (o único ponto de escrita: comentar não altera a planilha).
 */
export default function EntregaDrawer({ linha, precosVisiveis, onTogglePrecos, onClose }) {
  const aberto = Boolean(linha);
  const raw = linha?.raw || null;
  const etapas = React.useMemo(() => (raw ? buildAmbienteEtapas(raw) : []), [raw]);
  const rel = React.useMemo(
    () => (linha ? relativoEntrega(linha.dataLimite) : null),
    [linha]
  );
  const recebChips = React.useMemo(() => (raw ? resumoRecebimentos(raw) : []), [raw]);
  const marc = React.useMemo(
    () => (linha ? parseMarceneiro(linha.marceneiro) : null),
    [linha]
  );

  React.useEffect(() => {
    if (!aberto) return undefined;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aberto, onClose]);

  const conteudo = (
    <AnimatePresence>
      {aberto && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onMouseDown={onClose}
        >
          <Panel
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 34 }}
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Detalhes de ${linha.ambiente}`}
          >
            <Header>
              <HeaderTop>
                <Eyebrow>
                  {linha.previsao
                    ? "Previsão · sem lote · "
                    : linha.numeroLote && linha.numeroLote !== EMPTY
                      ? `Lote ${linha.numeroLote} · `
                      : ""}
                  PCP Etapa 14
                </Eyebrow>
                <CloseButton type="button" onClick={onClose} aria-label="Fechar">
                  <FiX size={16} />
                </CloseButton>
              </HeaderTop>
              <Titulo>{linha.ambiente}</Titulo>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <Subtitulo>{linha.cliente}</Subtitulo>
                <StatusPill $tone={linha.statusVariant}>{linha.status}</StatusPill>
              </div>
            </Header>

            <Body>
              <InfoGrid>
                <InfoCard>
                  <InfoLabel>Marceneiro</InfoLabel>
                  {marc.semMarceneiro ? (
                    <InfoSub $tone="warning" style={{ marginTop: 8 }}>
                      Sem marceneiro
                    </InfoSub>
                  ) : (
                    <>
                      <InfoValor style={{ fontSize: 14 }}>
                        <Avatar>{iniciais(marc.nome)}</Avatar>
                        <span>{marc.nome}</span>
                      </InfoValor>
                      {marc.tipo && <InfoSub>{marc.tipo}</InfoSub>}
                    </>
                  )}
                </InfoCard>

                <InfoCard>
                  <InfoLabel>
                    {linha.previsao ? "Entrega prevista em contrato" : "Entrega prevista"}
                  </InfoLabel>
                  <InfoValor>
                    {formatarData(linha.dataLimite) || EMPTY}
                  </InfoValor>
                  {rel && <InfoSub $tone={rel.tone}>{rel.texto}</InfoSub>}
                </InfoCard>

                <InfoCard>
                  <InfoTop>
                    <InfoLabel>Valor do ambiente</InfoLabel>
                    <EyeButton
                      type="button"
                      onClick={onTogglePrecos}
                      aria-label={precosVisiveis ? "Ocultar valor" : "Mostrar valor"}
                    >
                      {precosVisiveis ? <FiEye size={14} /> : <FiEyeOff size={14} />}
                    </EyeButton>
                  </InfoTop>
                  <InfoValor>
                    {precosVisiveis ? formatarMoeda(linha.valor) : "R$ ••••••"}
                  </InfoValor>
                </InfoCard>

                <InfoCard>
                  <InfoLabel>Recebimentos</InfoLabel>
                  {recebChips.length === 0 ? (
                    <InfoSub style={{ marginTop: 6 }}>Nenhum vinculado</InfoSub>
                  ) : (
                    <RecebChips>
                      {recebChips.map((chip) => (
                        <RecebChip key={chip.label} $tone={chip.tone}>
                          {chip.tone === "success" && <FiCheck size={11} />}
                          {chip.label}
                        </RecebChip>
                      ))}
                    </RecebChips>
                  )}
                </InfoCard>
              </InfoGrid>

              <div>
                <SectionLabel>Linha do tempo</SectionLabel>
                <TL>
                  {etapas.map((etapa, index) => (
                    <TLItem
                      key={etapa.key}
                      $status={etapa.status}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: 0.06 + index * 0.04,
                        duration: 0.3,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                    >
                      <TLDot $status={etapa.status}>{getTLIcon(etapa.status)}</TLDot>
                      <TLContent>
                        <TLTitulo $status={etapa.status}>{etapa.titulo}</TLTitulo>
                        <TLData $status={etapa.status}>{getDataLabel(etapa)}</TLData>
                        {etapa.status === "atual" && <TLBadge>Etapa atual</TLBadge>}
                      </TLContent>
                    </TLItem>
                  ))}
                </TL>
              </div>
              {raw && <HistoricoAmbiente row={raw} />}
            </Body>
          </Panel>
        </Overlay>
      )}
    </AnimatePresence>
  );

  if (typeof document === "undefined") return conteudo;
  return createPortal(conteudo, document.body);
}
